import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { TaskMapper, TaskFilterParams, Task } from '../../../../models/task.model';
import { Observable, Subject, shareReplay, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { SignalRService } from '@olv/ui-shared/services/signalr.service';
import { TaskTypeEnum, TaskTypeFilterEnum } from '@olv/ui-shared/enums/task.enums';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { TaskService } from '@olv/ui-shared/services/task.service';
import { TaskMerchantDto, TaskUsersFilterResponseDto } from '@olv/ui-shared/models/task.model';
import { FilterService, AppliedFilter } from '@olv/ui-shared/services/filter.service';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { enumToFilterFields } from '@olv/ui-shared/utils/enum-to-filter.util';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { MatDialog } from '@angular/material/dialog';
import { FiltersDialogComponent } from '@olv/ui-shared/ui-shared/filters-dialog/filters.component';
import { EmptyStateComponent } from '@olv/ui-shared/ui-shared/empty-state/empty-state.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-tasks-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PillSearchComponent,
    MatTooltipModule,
    LoadingSpinnerComponent,
    EmptyStateComponent,
    ButtonComponent,
  ],
  templateUrl: './tasks-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tasks-tab.component.scss'],
})
export class TasksTabComponent implements OnInit, OnChanges, OnDestroy {
  private merchantService = inject(MerchantService);
  private taskService = inject(TaskService);
  private signalRService = inject(SignalRService);
  private toastr = inject(ToastService);

  @Output() taskSelected = new EventEmitter<Task>();
  @Output() tasksLoaded = new EventEmitter<Task[]>();
  @Input() updateTick = 0;
  @Input() searchText = '';
  @Input() targetTaskId: string | null = null;

  tasks: Task[] = [];
  totalCount = 0;
  filteredTasks: Task[] = [];
  filtersApplied = false;
  loading = false;
  error: string | null = null;
  selectedTask: Task | null = null;
  TaskTypeEnum = TaskTypeEnum;
  isFirstLoad = true;
  isDisableFilters = false;

  private filterService = inject(FilterService);
  private readonly appCtx = inject(AppContextService);
  private dialog = inject(MatDialog);
  isSettlementApp = this.appCtx.getAppName() === 'settlement';

  filterMap: Partial<TaskFilterParams> = {};
  allFilters: TaskFilterParams = {};
  filters = [{ name: 'Task Type', filterFields: enumToFilterFields(this.getTaskTypeFilterEnum()) }];

  preparedByOptions: { value: string; label: string }[] = [];
  merchantOptions: { value: string; label: string }[] = [];

  private destroy$ = new Subject<void>();
  private seenTaskNotificationIds = new Set<string>();
  private notificationsPrimed = false;
  private taskUsers$!: Observable<TaskUsersFilterResponseDto>;
  private taskMerchants$!: Observable<TaskMerchantDto[]>;

  constructor() {
    this.signalRService.notifications$.pipe(takeUntil(this.destroy$)).subscribe((notifications) => {
      const taskNotificationIds = notifications
        .filter((n) => n.type === 'TaskCreated' || n.type === 'TaskResolved' || n.type === 'TaskUpdated')
        .map((n) => n.id);

      if (!this.notificationsPrimed) {
        this.notificationsPrimed = true;
        taskNotificationIds.forEach((id) => this.seenTaskNotificationIds.add(id));
        return;
      }

      const hasNew = taskNotificationIds.some((id) => !this.seenTaskNotificationIds.has(id));
      taskNotificationIds.forEach((id) => this.seenTaskNotificationIds.add(id));

      if (hasNew) {
        this.loadTasks();
      }
    });
  }

  ngOnInit(): void {
    if (this.isSettlementApp) {
      this.loadTaskMerchants();
    }
    this.loadTaskUsers();
    this.loadTasks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.loadTasks();
    }
    if (changes['updateTick'] && !changes['updateTick'].firstChange) {
      this.loadTasks();
    }
    if (changes['searchText'] && !changes['searchText'].firstChange) {
      this.loadTasks();
    }
    if (changes['targetTaskId'] && this.targetTaskId) {
      const target = this.tasks.find((task) => task.id === this.targetTaskId);
      if (target) {
        this.onTaskClick(target);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadTasks(): void {
    this.loading = true;
    this.error = null;

    const filterParams = {
      maxResultCount: 50,
      skipCount: 0,
      searchText: this.searchText || undefined,
      sorting: 'createdAt desc',
      ...this.filterMap,
    };
    this.allFilters = filterParams;

    this.taskService
      .getAllTasks(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (this.isFirstLoad) {
            this.isDisableFilters = response.items.length === 0 && !this.searchText?.trim();
          }
          this.isFirstLoad = false;
          this.tasks = response.items.map((dto) => TaskMapper.toFrontendTask(dto));
          this.totalCount = response.totalCount;
          this.tasksLoaded.emit(this.tasks);

          this.loading = false;
        },
        error: () => {
          this.error = 'Failed to load tasks. Please try again.';
          this.loading = false;
          this.showError('Failed to load tasks');
        },
      });
  }

  private showError(message: string): void {
    this.toastr.error(message);
  }

  onTaskClick(task: Task): void {
    this.taskSelected.emit(task);
    this.selectedTask = task;
    this.merchantService.openSidebar();
  }

  getTaskIcon(type: TaskTypeEnum): string {
    switch (type) {
      case TaskTypeEnum.Whitelist:
        return 'verified_user';
      case TaskTypeEnum.SystemWhitelist:
        return 'admin_panel_settings';
      case TaskTypeEnum.Deposit:
      case TaskTypeEnum.UnexpectedDeposit:
      case TaskTypeEnum.PaymentDepositAcknowledge:
      case TaskTypeEnum.SystemDeposit:
        return 'south';
      case TaskTypeEnum.Risk:
        return 'warning';
      case TaskTypeEnum.SystemRisk:
        return 'security';
      case TaskTypeEnum.Withdrawal:
      case TaskTypeEnum.SystemWithdrawal:
        return 'north';
      case TaskTypeEnum.Merchant:
        return 'business';
      case TaskTypeEnum.DelegationAllowanceDepleted:
        return 'battery_alert';
      case TaskTypeEnum.DelegationReserveDepleted:
        return 'error';
      case TaskTypeEnum.DelegationAllowanceTopUpOverSpend:
        return 'trending_up';
      case TaskTypeEnum.DelegationCapacityDrift:
        return 'sync_problem';
      case TaskTypeEnum.ExchangeRateRequired:
        return 'price_change';
      case TaskTypeEnum.PreLaunchEvmDeposit:
        return 'account_balance_wallet';
      case TaskTypeEnum.DelegationAllowanceUnderfunded:
        return 'trending_down';
      default:
        return 'task';
    }
  }

  getAvatarClass(type: TaskTypeEnum): string {
    switch (type) {
      case TaskTypeEnum.Whitelist:
        return 'avatar-whitelist';
      case TaskTypeEnum.SystemWhitelist:
        return 'avatar-system-whitelist';
      case TaskTypeEnum.Deposit:
      case TaskTypeEnum.UnexpectedDeposit:
      case TaskTypeEnum.PaymentDepositAcknowledge:
        return 'avatar-deposit';
      case TaskTypeEnum.SystemDeposit:
        return 'avatar-system-deposit';
      case TaskTypeEnum.Risk:
        return 'avatar-risk';
      case TaskTypeEnum.SystemRisk:
        return 'avatar-system-risk';
      case TaskTypeEnum.Withdrawal:
        return 'avatar-withdrawal';
      case TaskTypeEnum.SystemWithdrawal:
        return 'avatar-system-withdrawal';
      case TaskTypeEnum.Merchant:
        return 'avatar-merchant';
      case TaskTypeEnum.DelegationAllowanceDepleted:
      case TaskTypeEnum.DelegationReserveDepleted:
      case TaskTypeEnum.DelegationAllowanceTopUpOverSpend:
      case TaskTypeEnum.DelegationCapacityDrift:
      case TaskTypeEnum.DelegationAllowanceUnderfunded:
        return 'avatar-delegation';
      default:
        return 'avatar-default';
    }
  }

  getFirstTitlePart(title: string): string {
    if (title.includes('Deposit Available balance')) {
      return 'Deposit';
    } else if (title.includes('Withdrawal Available balance')) {
      return 'Withdrawal';
    }
    return title;
  }

  getSecondTitlePart(title: string): string {
    if (title.includes('Deposit Available balance') || title.includes('Withdrawal Available balance')) {
      return 'Available balance';
    }
    return '';
  }

  toggleFilters(): void {
    this.selectedTask = null;
    this.merchantService.closeSidebar();

    const dialogRef = this.dialog.open(FiltersDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        filters: this.filters,
        dropdownsOptions: this.getDropdownsOptions(),
        dateFiltersOptions: [{ key: 'preparedDate', label: 'Prepared Date' }],
        preselectedValues: this.getAppliedFiltersAsArray(),
        isDisableFilters: this.isDisabled,
      },
    });

    this.taskUsers$.pipe(takeUntil(dialogRef.afterClosed())).subscribe((response) => {
      const options = (response.preparedBy ?? []).map((user) => ({
        value: user.userId,
        label: user.displayName,
      }));
      dialogRef.componentInstance.updateDropdownOptions('preparedBy', options);
    });

    if (this.isSettlementApp) {
      this.taskMerchants$.pipe(takeUntil(dialogRef.afterClosed())).subscribe((items) => {
        const options = (items ?? []).map((merchant) => ({ value: merchant.id, label: merchant.name }));
        dialogRef.componentInstance.updateDropdownOptions('relatedMerchantId', options);
      });
    }

    const sub = dialogRef.componentInstance.resetRequest.subscribe((result) => {
      this.onFiltersApplied(result);
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onFiltersApplied(result);
      }

      sub.unsubscribe();
    });
  }

  clearFilters(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.loadTasks();
  }

  onFilterReset(): void {
    this.filterMap = {};
    this.filtersApplied = false;
    this.loadTasks();
  }

  getAppliedFiltersAsArray() {
    return this.filterService.getAppliedFilters(this.filterMap);
  }

  onFiltersApplied(values: AppliedFilter[]) {
    this.filtersApplied = values.length > 0;
    this.filterMap = this.filterService.applySelectedFilters(values, {});

    this.loadTasks();
  }

  private loadTaskMerchants(): void {
    this.taskMerchants$ = this.taskService.getTaskMerchants().pipe(shareReplay(1));
    this.taskMerchants$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        this.merchantOptions = (items ?? []).map((merchant) => ({ value: merchant.id, label: merchant.name }));
      },
    });
  }

  updateSearch(searchState: SearchState): void {
    if (this.searchText === searchState.value) {
      return;
    }

    this.searchText = searchState.value;
    this.loadTasks();
  }

  refreshTasks(): void {
    this.loadTasks();
  }

  isDepositOrWithdrawalTask(task: Task): boolean {
    return (
      task.type === TaskTypeEnum.Deposit ||
      task.type === TaskTypeEnum.Withdrawal ||
      task.type === TaskTypeEnum.UnexpectedDeposit
    );
  }

  isRiskTask(task: Task): boolean {
    return task.type === TaskTypeEnum.Risk;
  }

  private isDepositTask(task: Task): boolean {
    return (
      task.type === TaskTypeEnum.Deposit ||
      task.type === TaskTypeEnum.SystemDeposit ||
      task.type === TaskTypeEnum.UnexpectedDeposit ||
      task.type === TaskTypeEnum.PaymentDepositAcknowledge
    );
  }
  getRiskLevel(task: any): string | null {
    return task.whitelistedWalletDetail?.riskLevel ?? task.transactionDetail?.riskLevel ?? null;
  }
  getWalletName(task: Task): string | null {
    if (task.type === TaskTypeEnum.PreLaunchEvmDeposit) {
      return task.relatedWalletName || null;
    }

    // walletName is the sending wallet, only correct for withdrawals
    if (this.isDepositTask(task)) {
      return task.transactionDetail?.receivingWalletName || null;
    }

    return task.whitelistedWalletDetail?.walletName || task.transactionDetail?.walletName || null;
  }
  getAssignee(task: any): string {
    if (task.type === TaskTypeEnum.PreLaunchEvmDeposit) {
      return task.relatedMerchantName ?? 'System';
    }

    if (task.title === 'Whitelist Address Request') {
      return task.whitelistedWalletDetail?.merchantName ?? 'System';
    }

    if (
      task.title === 'Withdrawal Available Balance' ||
      task.title === 'Transaction Adjustment Created' ||
      task.type === TaskTypeEnum.PaymentDepositAcknowledge ||
      task.type === TaskTypeEnum.UnexpectedDeposit ||
      task.type === TaskTypeEnum.Deposit
    ) {
      return task.transactionDetail?.merchantName ?? 'System';
    }

    return task.initiatedByDisplayName;
  }
  get isDisabled(): boolean {
    if (this.isFirstLoad) {
      return this.loading;
    }

    return this.isDisableFilters;
  }
  private loadTaskUsers(): void {
    this.taskUsers$ = this.taskService.getTaskUsers().pipe(shareReplay(1));
    this.taskUsers$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.preparedByOptions = (response.preparedBy ?? []).map((user) => ({
          value: user.userId,
          label: user.displayName,
        }));
      },
    });
  }

  private getDropdownsOptions() {
    const dropdowns: any[] = [
      {
        key: 'preparedBy',
        label: 'Prepared By',
        placeholder: 'Select Prepared By',
        options: this.preparedByOptions,
        type: 'checkbox',
        isFilterClass: true,
        isSearch: true,
      },
    ];

    if (this.isSettlementApp) {
      dropdowns.push({
        key: 'relatedMerchantId',
        label: 'Merchants',
        placeholder: 'Select Merchant',
        options: this.merchantOptions,
        type: 'checkbox',
        isFilterClass: true,
        isSearch: true,
      });
    }

    return dropdowns;
  }

  private getTaskTypeFilterEnum() {
    if (this.isSettlementApp) {
      return {
        Whitelist: TaskTypeFilterEnum.Whitelist,
        Withdrawal: TaskTypeFilterEnum.Withdrawal,
        Deposit: TaskTypeFilterEnum.Deposit,
        'Merchant Change': TaskTypeFilterEnum.Merchant,
        Adjustment: TaskTypeFilterEnum.TransactionAdjustment,
        Reversal: TaskTypeFilterEnum.DepositReversal,
        'Unexpected Deposit': TaskTypeFilterEnum.UnexpectedDeposit,
        'Allowance Depleted': TaskTypeFilterEnum.DelegationAllowanceDepleted,
        'Reserve Depleted': TaskTypeFilterEnum.DelegationReserveDepleted,
        'Allowance Over-Spend': TaskTypeFilterEnum.DelegationAllowanceTopUpOverSpend,
        'Capacity Drift': TaskTypeFilterEnum.DelegationCapacityDrift,
        'Exchange Rate Required': TaskTypeFilterEnum.ExchangeRateRequired,
        'Pre-Launch EVM Deposit': TaskTypeFilterEnum.PreLaunchEvmDeposit,
        'Allowance Underfunded': TaskTypeFilterEnum.DelegationAllowanceUnderfunded,
      };
    }

    return {
      Whitelist: TaskTypeFilterEnum.Whitelist,
      Withdrawal: TaskTypeFilterEnum.Withdrawal,
    };
  }
}
